import axios from 'axios';
import { Blog, CreateBlogDto, UpdateBlogDto, BlogListResponse, BlogStats, BlogStatus } from '../types/blog';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const blogService = {
  // Public endpoints
  getPublishedBlogs: async (page = 1, limit = 10, search?: string): Promise<BlogListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) params.append('search', search);
    
    const response = await api.get(`/blog/public?${params}`);
    return response.data;
  },

  getBlogBySlug: async (slug: string): Promise<Blog> => {
    const response = await api.get(`/blog/public/slug/${slug}`);
    return response.data;
  },

  getTags: async (): Promise<string[]> => {
    const response = await api.get('/blog/public/tags');
    return response.data;
  },

  // Protected endpoints
  createBlog: async (blogData: CreateBlogDto): Promise<Blog> => {
    const response = await api.post('/blog', blogData);
    return response.data;
  },

  getAllBlogs: async (
    page = 1,
    limit = 10,
    status?: BlogStatus,
    authorId?: string,
    search?: string
  ): Promise<BlogListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (status) params.append('status', status);
    if (authorId) params.append('author', authorId);
    if (search) params.append('search', search);
    
    const response = await api.get(`/blog?${params}`);
    return response.data;
  },

  getMyBlogs: async (
    page = 1,
    limit = 10,
    status?: BlogStatus,
    search?: string
  ): Promise<BlogListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    
    const response = await api.get(`/blog/my-blogs?${params}`);
    return response.data;
  },

  getBlogById: async (id: string): Promise<Blog> => {
    const response = await api.get(`/blog/${id}`);
    return response.data;
  },

  updateBlog: async (id: string, blogData: UpdateBlogDto): Promise<Blog> => {
    const response = await api.patch(`/blog/${id}`, blogData);
    return response.data;
  },

  updateBlogStatus: async (id: string, status: BlogStatus): Promise<Blog> => {
    const response = await api.patch(`/blog/${id}/status`, { status });
    return response.data;
  },

  updateBlogPriority: async (id: string, priority: number): Promise<Blog> => {
    const response = await api.patch(`/blog/${id}/priority`, { priority });
    return response.data;
  },

  deleteBlog: async (id: string): Promise<void> => {
    await api.delete(`/blog/${id}`);
  },

  getBlogStats: async (): Promise<BlogStats> => {
    const response = await api.get('/blog/stats');
    return response.data;
  },
}; 